import { useState, useEffect } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material'
import { Close as CloseIcon, History as HistoryIcon } from '@mui/icons-material'
import { publishApi, ActivityLogEntry } from '../../services/api'

interface PublishHistoryDrawerProps {
  open: boolean
  onClose: () => void
  clientId: string
}

const EVENT_LABELS: Record<string, string> = {
  PUBLISH_READY: 'Published to Client',
  PUBLISH_DRAFT: 'Published as Draft',
  UNPUBLISH: 'Unpublished',
  SOURCE_CHANGED: 'Resume Source Changed',
  CLIENT_APPROVED: 'Client Approved',
  STAFF_APPROVED: 'Staff Approved',
}

const STATE_CHIP_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'default' | 'info' | 'primary' }> = {
  published: { label: 'Published', color: 'success' },
  draft: { label: 'Draft', color: 'warning' },
  client_approved: { label: 'Approved', color: 'info' },
  staff_approved: { label: 'Staff Approved', color: 'primary' },
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function PublishHistoryDrawer({ open, onClose, clientId }: PublishHistoryDrawerProps) {
  const [loading, setLoading] = useState(false)
  const [entries, setEntries] = useState<ActivityLogEntry[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await publishApi.getResumeHistory(clientId)
        // Sort by createdAt descending (API should already do this, but ensure it)
        const sorted = Array.isArray(data)
          ? data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          : []
        setEntries(sorted)
      } catch (err) {
        console.error('Failed to load publish history:', err)
        setError('Failed to load publish history')
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [open, clientId])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          maxWidth: '100vw',
          // Sit below the fixed 64px AppBar so the drawer header + close
          // button aren't clipped by the top nav.
          mt: '64px',
          height: 'calc(100% - 64px)',
          backgroundColor: 'background.paper',
          color: 'text.primary',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">Publish History</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && entries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No publish history yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              History will appear here when the resume is published or unpublished.
            </Typography>
          </Box>
        )}

        {!loading && !error && entries.length > 0 && (
          <List disablePadding>
            {entries.map((entry, index) => {
              const chipConfig = STATE_CHIP_CONFIG[entry.resultingState]
              return (
                <ListItem
                  key={entry.id}
                  sx={{
                    borderBottom: index < entries.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    alignItems: 'flex-start',
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {EVENT_LABELS[entry.eventType] || entry.eventType}
                        </Typography>
                        {chipConfig && (
                          <Chip
                            label={chipConfig.label}
                            color={chipConfig.color}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {entry.userDisplayName || entry.userEmail} &middot; {formatTimestamp(entry.createdAt)}
                      </Typography>
                    }
                  />
                </ListItem>
              )
            })}
          </List>
        )}
      </Box>
    </Drawer>
  )
}
